<template>
  <div class="device-firmware-list">
    <template v-if="isDeviceListLoading">
      <div class="not-searched">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".v-table__wrapper"
    >
      <div class="table-scrollbar">
        <v-table>
          <thead>
          <tr class="table-header-row">
            <th>
              <div class="checkbox-in-grid align-middle">
                <v-checkbox
                  v-model="selectAll"
                  @change="toggleSelectAll"
                  hide-details
                />
              </div>
            </th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.deviceFirmware.list.operationOffice')"
                :sort-field="sortField.ORDER_BY_OPERATION_OFFICE"
                :current-sort-field="deviceListSort.sort"
                :current-sort-type="deviceListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              {{ i18n.global.t('pages.deviceFirmware.list.numberPlate') }}
            </th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.deviceFirmware.list.deviceId')"
                :sort-field="sortField.ORDER_BY_DEVICE_ID"
                :current-sort-field="deviceListSort.sort"
                :current-sort-type="deviceListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              {{ i18n.global.t('pages.deviceFirmware.list.modelNumber') }}
            </th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.deviceFirmware.list.typeNumber')"
                :sort-field="sortField.ORDER_BY_DEVICE_TYPE"
                :current-sort-field="deviceListSort.sort"
                :current-sort-type="deviceListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              {{ i18n.global.t('pages.deviceFirmware.list.version') }}
            </th>
            <th>
              {{ i18n.global.t('pages.deviceFirmware.applyDate') }}
            </th>
          </tr>
          </thead>
          <tbody>
            <template v-if="devices.length > 0">
              <tr
                v-for="(device, index) in devices"
                :key="index"
              >
                <td>
                  <v-checkbox
                    class="initialism align-middle checkbox-in-grid"
                    label=''
                    v-model="this.selectedDevices[this.makeKey(device)]"
                  />
                </td>
                <td>{{ device.operation_office_company_name || '-' }}</td>
                <td>{{ device?.vehicle?.number_plate || '-' }}</td>
                <td>{{ device.device_id }}</td>
                <td>{{ device?.model_number }}</td>
                <td>{{ device?.type?.type || '-' }}</td>
                <td>{{ device?.firmware?.version || '-' }}</td>
                <td>{{
                    device?.firmware?.activate_date ? formatDate(device.firmware.activate_date) : '-'
                  }}
                </td>
              </tr>
            </template>
            <template v-else-if="!isDeviceListLoading">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
    <hr/>
  </div>
</template>

<script lang="ts">

import {defineComponent, PropType} from "vue";

import ResizableList from '@/components/ResizableList.vue';
import {formatDate} from "@/services/functions";
import {sortType} from "@/services/consts";
import DeviceFirmware from "@/types/DeviceFirmware";
import {sortField} from "@/types/requests/getDeviceFirmwareListRequest";
import TableSort from "@/components/template/TableSort.vue";
import i18n from '@/lang/i18n';

export default defineComponent({
  setup() {
    return {
      formatDate,
      sortField,
      sortType,
      i18n
    }
  },
  data() {
    return {
      deviceListSort: {
        sort_type: undefined as string | undefined,
        sort: undefined as string | undefined,
      },
      selectedDevices: {} as { [key: string]: boolean },
      selectAll: false as boolean,
    }
  },
  props: {
    isDeviceListLoading: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    devices: {
      type: Array as PropType<DeviceFirmware[]>,
      required: true,
    },
  },
  emits: ['sort-change', 'update-device-click', 'update-selected-items'],
  name: "DeviceList",
  computed: {},
  watch: {
    deviceListSort: {
      handler() {
        this.$emit('sort-change', this.deviceListSort)
      },
      deep: true
    },
    devices: {
      handler() {
        this.initSelectedDevices()
      },
    },
    selectedDevices: {
      handler() {
        const selectedDevices: Array<string> = []
        Object.keys(this.selectedDevices).forEach((key) => {
          if (this.selectedDevices[key]) {
            selectedDevices.push(key)
          }
        })
        this.$emit('update-selected-items', selectedDevices)
      },
      deep: true
    },
  },
  methods: {
    toggleSort(sortFiledValue: string) {

      let updatedSortField = false;
      if (sortFiledValue !== this.deviceListSort.sort) {
        updatedSortField = true
      }

      this.deviceListSort.sort = sortFiledValue;
      if (this.deviceListSort.sort_type === undefined || updatedSortField) {
        this.deviceListSort.sort_type = sortType.ASK
      } else {
        this.deviceListSort.sort_type = this.deviceListSort.sort_type === sortType.DESC ? sortType.ASK : sortType.DESC
      }
    },
    changeSort(sortFiledValue: string, needSortType: sortType): void {
      if ( this.deviceListSort.sort == sortFiledValue && this.deviceListSort.sort_type == needSortType ){
        return
      }
      this.deviceListSort.sort = sortFiledValue;
      this.deviceListSort.sort_type = needSortType
    },
    initSelectedDevices(): void {
      this.selectedDevices = {}
      this.selectAll = false
      this.devices.map(device => {
        const key = this.makeKey(device)
        this.selectedDevices[key] = false
      })
    },
    makeKey(device: DeviceFirmware): string {
      const firmwareId = device?.firmware?.id;
      const deviceId = device.id;
      return `${deviceId}.${firmwareId}`;
    },
    toggleSelectAll(): void {
      Object.keys(this.selectedDevices).forEach((key) => {
        this.selectedDevices[key] = this.selectAll
      })
    }
  },
  components: {
    ResizableList,
    TableSort,
  }
})
</script>

<style>
.device-firmware-list .v-table__wrapper {
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
</style>
<style scoped>
.device-firmware-list {
  position: relative;
}

.device-firmware-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  min-width: 7em;
}
.device-firmware-list table .table-header-row th {
  z-index: 1;
}
.device-firmware-list table .table-header-row th:first-child {
  z-index: 2;
}
</style>
